import type { InterviewSectionDefinition } from "@repo/app-types/entities/interviewSection/definition";
import type { TrainingParticipant } from "@repo/app-types/entities/trainingParticipant";
import type { FC } from "react";
import { Tree, TreeItemContent, type Selection } from "react-aria-components";
import { ChapterItemChevronButton } from "../courses/chapterItemChevronButton.js";
import { ChapterItemIndex } from "../courses/chapterItemIndex.js";
import { ChapterItemTitle } from "../courses/chapterItemTitle.js";
import { ChapterTreeItem } from "../courses/chapterTreeItem.js";
import { LessonItemIcon } from "../courses/lessonItemIcon.js";
import { LessonItemIndex } from "../courses/lessonItemIndex.js";
import { LessonItemStatusIndicator } from "../courses/lessonItemStatusIndicator.js";
import { LessonItemTitle } from "../courses/lessonItemTitle.js";
import { LessonTreeItem } from "../courses/lessonTreeItem.js";
import { useCourseTreeExpandAndCollapse } from "../projects/projectSettings/training/courseTreeHelpers.js";
import { ChapterAssessmentCell } from "./chapterAssessmentCell.js";

export interface ParticipantCourseTreeProps {
  treeLabelId: string;
  sectionsById: Map<string, InterviewSectionDefinition> | undefined;
  participant: TrainingParticipant;
  selectedKeys: readonly string[];
  onSelectionChange: (newSelection: Selection) => void;
}

export const ParticipantCourseTree: FC<ParticipantCourseTreeProps> = (
  props,
) => {
  const { participant } = props;

  const { expandedKeys, onExpandedChange } = useCourseTreeExpandAndCollapse(
    participant.course,
  );

  return (
    <Tree
      aria-labelledby={props.treeLabelId}
      expandedKeys={expandedKeys}
      onExpandedChange={onExpandedChange}
      selectionMode="single"
      selectionBehavior="replace"
      disallowEmptySelection
      selectedKeys={props.selectedKeys}
      onSelectionChange={props.onSelectionChange}
      disabledBehavior="selection"
      autoFocus
    >
      {participant.course.chapters.map((chapter, chapterIndex) => (
        <ChapterTreeItem
          key={chapter.id}
          chapter={chapter}
          index={chapterIndex}
          // isDisabled={!participant.assessment?.chapters[chapter.id]}
        >
          <TreeItemContent>
            <div className="flex items-center space-x-4 px-4 py-2">
              <ChapterItemIndex index={chapterIndex} />
              <ChapterItemTitle index={chapterIndex} chapter={chapter} />
              <ChapterAssessmentCell
                assessment={participant.assessment?.chapters[chapter.id]}
              />
              <ChapterItemChevronButton />
            </div>
          </TreeItemContent>

          {chapter.lessons.map(
            (lesson, lessonIndex, { length: lessonCount }) => {
              const section = props.sectionsById?.get(
                lesson.interviewSectionDefinitionId,
              );

              return (
                <LessonTreeItem
                  key={lesson.interviewSectionDefinitionId}
                  sectionId={lesson.interviewSectionDefinitionId}
                  section={section}
                  isLastItemAtLevel={lessonIndex === lessonCount - 1}
                  // isDisabled={
                  //   mostCompleteStatus === InterviewSectionStatus.Pending
                  // }
                >
                  <TreeItemContent>
                    <div className="flex items-center space-x-4 px-4 py-2">
                      <LessonItemIndex index={lessonIndex} />
                      <LessonItemIcon type={section?.settings.type} />
                      <LessonItemTitle section={section} />
                      <LessonItemStatusIndicator status={lesson.status} />
                    </div>
                  </TreeItemContent>
                </LessonTreeItem>
              );
            },
          )}
        </ChapterTreeItem>
      ))}
    </Tree>
  );
};
